import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AbasDashboard } from './DashboardEquipe';
import { api, mensagemErro } from '../api';
import { useAuth } from '../auth';
import { Carregando, Erro, Icon, Progresso } from '../components/ui';
import { moeda, num } from '../format';

interface Dash {
  escopo: 'TENANT' | 'VENDEDOR';
  kpis: Record<string, number | string>;
  campanhas: Array<{ id: string; nome: string; total: number; enviados: number; pendentesMeus: number; oportunidades: number }>;
  usoPlano: { usados: number; limite: number | null };
}

function Kpi({ label, valor, sub }: { label: string; valor: string; sub?: string }) {
  return (
    <div className="card kpi">
      <span className="label">{label}</span>
      <span className="value">{valor}</span>
      {sub && <span className="small muted">{sub}</span>}
    </div>
  );
}

export function DashboardPage() {
  const { me } = useAuth();
  const q = useQuery({ queryKey: ['dashboard'], queryFn: () => api<Dash>('/dashboard') });
  if (q.isLoading) return <Carregando />;
  if (q.error) return <Erro erro={mensagemErro(q.error)} />;
  const d = q.data!;
  const k = d.kpis;
  const pctUso = d.usoPlano.limite ? Math.round((d.usoPlano.usados / d.usoPlano.limite) * 100) : 0;
  const mes = new Date().toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{d.escopo === 'TENANT' ? 'Dashboard' : `Olá, ${me?.usuario.nome.split(' ')[0]}`}</h1>
          <span className="muted small">{mes} · {d.escopo === 'TENANT' ? 'toda a empresa' : 'seus números'}</span>
        </div>
        <div className="actions">
          <Link className="btn" to="/clientes/novo"><Icon name="mais" size={15} />Novo cliente</Link>
          <Link className="btn primary" to="/campanhas/nova"><Icon name="campanhas" size={15} />Nova campanha</Link>
        </div>
      </div>

      <AbasDashboard />

      {pctUso >= 80 && (
        <div className="alert warn"><Icon name="alerta" /><span>Você usou {pctUso}% dos contatos do mês ({num(d.usoPlano.usados)} de {num(d.usoPlano.limite)}). Fale com o comercial para ampliar o plano.</span></div>
      )}
      {Number(k.clientesSemVendedor) > 0 && (
        <Link to="/clientes?semVendedor=true" className="alert warn" style={{ color: 'var(--amber-text)' }}>
          <Icon name="alerta" /><span>{num(Number(k.clientesSemVendedor))} clientes sem vendedor. Distribua a carteira para que entrem nas campanhas.</span>
        </Link>
      )}

      <div className="grid-kpi">
        <Kpi label="Clientes ativos" valor={num(Number(k.clientesAtivos))} />
        <Kpi label="Contatos hoje" valor={num(Number(k.contatosHoje))} sub={`${num(Number(k.contatosMes))} no mês`} />
        <Kpi label="Clientes contatados no mês" valor={num(Number(k.clientesContatadosMes))} />
        {k.negociacaoAtiva ? (
          <Link to="/negociacoes" className="kpi-link" aria-label="Ver negociações">
            <Kpi label="Em negociação (pipeline)" valor={moeda(k.pipelineAberto)}
              sub={`${num(Number(k.oportunidadesAbertas))} abertas${Number(k.negociacoesVencidas) ? ` · ${num(Number(k.negociacoesVencidas))} para cobrar` : ''}`} />
          </Link>
        ) : (
          <Kpi label="Oportunidades abertas" valor={num(Number(k.oportunidadesAbertas))} sub={`${moeda(k.valorAberto)} em aberto`} />
        )}
        <Kpi label="Vendas no mês" valor={num(Number(k.vendasMes))} sub={moeda(k.receitaMes)} />
        {k.negociacaoAtiva && <Kpi label="Perdido no mês" valor={moeda(k.perdidoMes)} sub={`${num(Number(k.perdidasMes))} negociações · não entra em vendas`} />}
        <Link to="/retornos" className="kpi-link" aria-label="Ver retornos pendentes"><Kpi label="Retornos pendentes" valor={num(Number(k.followUps))} sub="atrasados + hoje · ver lista" /></Link>
      </div>

      <section className="card">
        <div className="card-head">
          <h2>Campanhas em execução</h2>
          <Link to="/campanhas" className="small">Ver todas</Link>
        </div>
        {d.campanhas.length === 0 ? (
          <div className="empty">
            Nenhuma campanha rodando.
            <Link className="btn primary" to="/campanhas/nova">Criar campanha</Link>
          </div>
        ) : (
          <div className="grid-kpi" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
            {d.campanhas.map((c) => (
              <div key={c.id} className="card" style={{ background: 'var(--surface-2)', padding: 14, gap: 10 }}>
                <div className="card-head">
                  <Link to={`/campanhas/${c.id}`} style={{ color: 'var(--text)', fontWeight: 500 }}>{c.nome}</Link>
                  <span className="num small">{c.total ? Math.round((c.enviados / c.total) * 100) : 0}%</span>
                </div>
                <Progresso valor={c.enviados} total={c.total} />
                <span className="num small muted">{c.enviados} de {c.total} enviados · {c.oportunidades} oportunidades</span>
                {c.pendentesMeus > 0 && (
                  <Link className="btn primary sm" to={`/campanhas/${c.id}/fila`}>Continuar minha fila ({c.pendentesMeus})</Link>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </>
  );
}
